import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { sanitizeColor } from "@/lib/sanitize-color";
import { copyToClipboard } from "@/utils/copy-to-clipboard";

export type ResellerDetailData = {
	emails: string[];
	logo: string | null;
	primary_color: string | null;
	secondary_color: string | null;
	is_confirmed: boolean;
	managers_count: number;
	users_count: number;
	created_at: string;
	updated_at: string;
};

interface DetailViewProps {
	data: ResellerDetailData;
}

export function DetailView({ data }: DetailViewProps) {
	const { t, i18n } = useTranslation();
	const copiedMessage = t("resellers.copied");
	const copyErrorMessage = t("resellers.copyError");

	return (
		<>
			<div className="flex items-start justify-between gap-4 min-w-0">
				<span className="text-muted-foreground text-sm shrink-0">E-maile</span>
				<div className="flex flex-col items-end gap-0.5">
					{data.emails.length > 0 ? (
						data.emails.map((email) => (
							<span key={email} className="text-sm font-medium truncate">
								{email}
							</span>
						))
					) : (
						<span className="text-sm font-medium">—</span>
					)}
				</div>
			</div>
			<Row label="Logo (URL)" value={data.logo ?? "—"} copyable />
			<ColorRow
				label={t("resellers.primaryColor")}
				value={data.primary_color}
				copiedMessage={copiedMessage}
				copyErrorMessage={copyErrorMessage}
			/>
			<ColorRow
				label={t("resellers.secondaryColor")}
				value={data.secondary_color}
				copiedMessage={copiedMessage}
				copyErrorMessage={copyErrorMessage}
			/>
			<div className="flex items-center justify-between gap-4">
				<span className="text-muted-foreground text-sm">
					{t("mqttSources.colStatus")}
				</span>
				<Badge variant={data.is_confirmed ? "default" : "outline"}>
					{data.is_confirmed
						? t("resellers.statusConfirmed")
						: t("resellers.statusUnconfirmed")}
				</Badge>
			</div>
			<Row
				label={t("resellers.colManagersCount")}
				value={String(data.managers_count)}
			/>
			<Row
				label={t("resellers.colUsersCount")}
				value={String(data.users_count)}
			/>
			<Row
				label={t("resellers.createdAt")}
				value={new Date(data.created_at).toLocaleDateString(i18n.language)}
			/>
			<Row
				label={t("resellers.updatedAt")}
				value={new Date(data.updated_at).toLocaleDateString(i18n.language)}
			/>
		</>
	);
}

interface RowProps {
	label: string;
	value: string;
	copyable?: boolean;
}

function Row({ label, value, copyable }: RowProps) {
	const { t } = useTranslation();
	const isPlaceholder = value === "—";
	const canCopy = copyable && !isPlaceholder;

	return (
		<div className="flex items-center justify-between gap-4 min-w-0">
			<span className="text-muted-foreground text-sm shrink-0">{label}</span>
			<Tooltip>
				<TooltipTrigger asChild>
					{canCopy ? (
						<button
							type="button"
							className="text-sm font-medium truncate cursor-pointer hover:text-primary transition-colors"
							onClick={() =>
								copyToClipboard(value, {
									successMessage: t("resellers.copied"),
									errorMessage: t("resellers.copyError"),
								})
							}
						>
							{value}
						</button>
					) : (
						<span className="text-sm font-medium truncate">{value}</span>
					)}
				</TooltipTrigger>
				<TooltipContent className="max-w-80 break-all">{value}</TooltipContent>
			</Tooltip>
		</div>
	);
}

interface ColorRowProps {
	label: string;
	value: string | null;
	copiedMessage: string;
	copyErrorMessage: string;
}

function ColorRow({
	label,
	value,
	copiedMessage,
	copyErrorMessage,
}: ColorRowProps) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-muted-foreground text-sm">{label}</span>
			{value ? (
				<button
					type="button"
					className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
					onClick={() =>
						copyToClipboard(value, {
							successMessage: copiedMessage,
							errorMessage: copyErrorMessage,
						})
					}
				>
					<span
						className="size-4 rounded-full border"
						style={{
							background: sanitizeColor(value) || undefined,
						}}
					/>
					<span className="text-sm font-medium">{value}</span>
				</button>
			) : (
				<span className="text-sm font-medium">—</span>
			)}
		</div>
	);
}
